import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useExchangeRates } from "../context/ExchangeRateContext";
import { formatNumber } from "../utils/formatters";
import "./Header.css";

interface HeaderProps {
  onMenuClick?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { user, logout } = useAuth();
  const { usdRate, eurRate, loading } = useExchangeRates();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Dışarı tıklandığında menüyü kapat
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        userMenuRef.current &&
        !userMenuRef.current.contains(event.target as Node)
      ) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Kullanıcının baş harflerini al
  const getInitials = () => {
    if (!user) return "?";
    const first = user.firstName?.charAt(0) || "";
    const last = user.lastName?.charAt(0) || "";
    return (first + last).toUpperCase() || "?";
  };

  // Rol badge'i için renk
  const getRoleBadgeClass = () => {
    switch (user?.role?.toLowerCase()) {
      case "admin":
        return "role-admin";
      case "manager":
        return "role-manager";
      case "modelist":
        return "role-modelist";
      default:
        return "role-user";
    }
  };

  // Rol ismini Türkçe'ye çevir
  const getRoleName = () => {
    switch (user?.role?.toLowerCase()) {
      case "admin":
        return "Yönetici";
      case "manager":
        return "Müdür";
      case "modelist":
        return "Modelist";
      case "accounting":
        return "Muhasebe";
      default:
        return user?.role || "Kullanıcı";
    }
  };

  return (
    <header className="header">
      <div className="header-container">
        {/* Sol Bölüm - Menü Butonu ve Logo */}
        <div className="header-left">
          {onMenuClick && (
            <button
              onClick={onMenuClick}
              className="header-menu-btn"
              aria-label="Menüyü Aç"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
          )}

          <div className="header-brand">
            <img
              src="/Images/logo-siyah.png"
              alt="Logo"
              className="header-logo"
            />
            <div className="header-divider"></div>
          </div>
        </div>

        {/* Sağ Bölüm - Kullanıcı */}
        <div className="header-right">
          {user && (
            <div className="header-user-section" ref={userMenuRef}>
              <button
                className="header-user-btn"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar-wrapper">
                  <div className="user-avatar">{getInitials()}</div>
                  <span
                    className={`user-status-dot ${getRoleBadgeClass()}`}
                  ></span>
                </div>
                <div className="user-details">
                  <span className="user-name">
                    {user.firstName} {user.lastName}
                  </span>
                  <span className={`user-role ${getRoleBadgeClass()}`}>
                    {getRoleName()}
                  </span>
                </div>
                <svg
                  className={`user-chevron ${showUserMenu ? "open" : ""}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="dropdown-header">
                    <div className="dropdown-avatar">{getInitials()}</div>
                    <div className="dropdown-info">
                      <span className="dropdown-name">
                        {user.firstName} {user.lastName}
                      </span>
                      <span className="dropdown-email">{user.email}</span>
                    </div>
                  </div>

                  {/* Döviz Kurları - Dropdown içinde */}
                  {!loading && (usdRate || eurRate) && (
                    <>
                      <div className="dropdown-divider"></div>
                      <div className="dropdown-rates">
                        <div className="dropdown-rates-title">
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <line x1="12" y1="1" x2="12" y2="23" />
                            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                          </svg>
                          <span>Güncel Kurlar</span>
                        </div>
                        <div className="dropdown-rates-list">
                          {usdRate && (
                            <div className="dropdown-rate-item">
                              <div className="dropdown-rate-flag rate-usd">
                                $
                              </div>
                              <span className="dropdown-rate-code">
                                USD/TRY
                              </span>
                              <span className="dropdown-rate-value">
                                {formatNumber(usdRate)} ₺
                              </span>
                            </div>
                          )}
                          {eurRate && (
                            <div className="dropdown-rate-item">
                              <div className="dropdown-rate-flag rate-eur">
                                €
                              </div>
                              <span className="dropdown-rate-code">
                                EUR/TRY
                              </span>
                              <span className="dropdown-rate-value">
                                {formatNumber(eurRate)} ₺
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-menu">
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        navigate("/change-password");
                        setShowUserMenu(false);
                      }}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <rect
                          x="3"
                          y="11"
                          width="18"
                          height="11"
                          rx="2"
                          ry="2"
                        />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                      <span>Şifre Değiştir</span>
                    </button>
                  </div>

                  <div className="dropdown-divider"></div>

                  <button className="dropdown-logout" onClick={handleLogout}>
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    <span>Çıkış Yap</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
